import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { BrandLogo, BrandRecord } from '@/types'
import { deriveColors, normalizeHex } from '@/lib/brand-colors'

/**
 * The operator's brand record on /u/<alias> (logbook decision 26): read,
 * cached, and put on the page as the four `--pkz-brand-*` custom properties
 * that index.css maps onto the map's own tokens.
 *
 * The record endpoint is being upgraded. Until it is deployed it answers only
 * `{alias, displayName, brandLogoUrl, brandPrimaryColor}`; `parseRecord` reads
 * either shape into a v1 record, so nothing past this file knows the old one.
 *
 * A branded page is always light: it takes `.dark` off the root while it is
 * shown, so the basemap renders in day mode and the operator's light logo
 * always sits on a light header. That is why the components use the light
 * logo variants only. Without an alias, or when its record cannot be had, the
 * page is Parkeze's and follows the system theme (use-theme.ts).
 */

/**
 * The cache key carries the record's schema version, so a blob written in an
 * older shape (the `parkeze-branding-<alias>` keys) is never read; it is
 * removed when the same alias is cached again.
 */
const CACHE_SCHEMA = 1
const CACHE_PREFIX = `parkeze-brand:v${CACHE_SCHEMA}:`
const LEGACY_CACHE_PREFIX = 'parkeze-branding-'

/** The endpoint's own `max-age`: a branding change shows within five minutes. */
const STALE_MS = 5 * 60 * 1000

const BRAND_PROPERTIES = {
  '--pkz-brand-primary': 'primary',
  '--pkz-brand-on-primary': 'on_primary',
  '--pkz-brand-primary-text': 'primary_text',
  '--pkz-brand-primary-dark': 'primary_dark',
} as const

function getRealmFromPath(): string | null {
  const match = window.location.pathname.match(/^\/u\/([^/]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

/** An https URL, or null. A logo is only ever served from Parkeze's asset host. */
function httpsUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null
  try {
    return new URL(value).protocol === 'https:' ? value : null
  } catch {
    return null
  }
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function parseLogo(value: unknown): BrandLogo | null {
  if (!value || typeof value !== 'object') return null
  const v = value as Record<string, unknown>
  const png = httpsUrl(v.png)
  if (!png) return null
  const aspect = typeof v.aspect === 'number' && v.aspect > 0 && Number.isFinite(v.aspect) ? v.aspect : null
  return { svg: httpsUrl(v.svg), png, aspect }
}

/**
 * Reads an answer from the record endpoint, in either shape, as a v1 record,
 * or null when it is neither.
 *
 * The legacy shape has no `colors`, so they are derived here with the
 * contract's own arithmetic (lib/brand-colors.ts); a v1 answer's colors are
 * used as given. Its one logo, `brandLogoUrl`, is a hot-linked URL of any
 * format. The map has always shown it alone as the operator's identity, so it
 * is read as the wordmark, with the name as its alt text (the reservation
 * portal reads it the same way).
 */
function parseRecord(data: unknown, alias: string): BrandRecord | null {
  if (!data || typeof data !== 'object') return null
  const d = data as Record<string, unknown>
  const name = text(d.name) ?? text(d.displayName)
  if (!name) return null

  const given = d.colors && typeof d.colors === 'object' ? (d.colors as Record<string, unknown>) : null
  const primary = normalizeHex(given?.primary)
  const onPrimary = normalizeHex(given?.on_primary)
  const primaryText = normalizeHex(given?.primary_text)
  const primaryDark = normalizeHex(given?.primary_dark)
  const colors =
    primary && onPrimary && primaryText && primaryDark
      ? { primary, on_primary: onPrimary, primary_text: primaryText, primary_dark: primaryDark }
      : deriveColors(given?.primary ?? d.brandPrimaryColor)

  let logo: BrandRecord['logo']
  if (d.logo && typeof d.logo === 'object') {
    const l = d.logo as Record<string, unknown>
    logo = {
      wordmark: parseLogo(l.wordmark),
      wordmark_dark: parseLogo(l.wordmark_dark),
      mark: parseLogo(l.mark),
      mark_dark: parseLogo(l.mark_dark),
    }
  } else {
    const legacy = httpsUrl(d.brandLogoUrl)
    logo = {
      wordmark: legacy ? { svg: null, png: legacy, aspect: null } : null,
      wordmark_dark: null,
      mark: null,
      mark_dark: null,
    }
  }

  const support = d.guest_support && typeof d.guest_support === 'object'
    ? (d.guest_support as Record<string, unknown>)
    : {}

  return {
    version: 1,
    alias: text(d.alias) ?? alias,
    name,
    short_name: text(d.short_name) ?? name,
    colors,
    logo,
    guest_support: { email: text(support.email), phone: text(support.phone), url: httpsUrl(support.url) },
    powered_by: typeof d.powered_by === 'boolean' ? d.powered_by : alias !== 'parkeze',
    updated_at: text(d.updated_at),
  }
}

interface CachedRecord {
  record: BrandRecord
  fetchedAt: number
}

function readCache(realm: string): CachedRecord | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + realm)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { record?: unknown; fetchedAt?: unknown }
    // Read back through the parser, so an edited or truncated blob is ignored.
    const record = parseRecord(parsed.record, realm)
    const fetchedAt = typeof parsed.fetchedAt === 'number' ? parsed.fetchedAt : 0
    return record ? { record, fetchedAt } : null
  } catch {
    return null
  }
}

function writeCache(realm: string, record: BrandRecord): void {
  try {
    localStorage.setItem(CACHE_PREFIX + realm, JSON.stringify({ record, fetchedAt: Date.now() }))
    localStorage.removeItem(LEGACY_CACHE_PREFIX + realm)
  } catch {
    // Storage can be full or refused (private windows); the record still renders.
  }
}

function applyBrandToRoot(record: BrandRecord): void {
  const root = document.documentElement
  // Branded pages are light (see the top of this file).
  root.classList.remove('dark')
  for (const [property, key] of Object.entries(BRAND_PROPERTIES)) {
    root.style.setProperty(property, record.colors[key])
  }
}

function removeBrandFromRoot(): void {
  const root = document.documentElement
  for (const property of Object.keys(BRAND_PROPERTIES)) root.style.removeProperty(property)
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) root.classList.add('dark')
}

async function fetchRecord(realm: string): Promise<BrandRecord> {
  const res = await fetch(
    `https://auth.parkeze.com/realms/parkeze/brand/orgs/${encodeURIComponent(realm)}`
  )
  if (!res.ok) throw new Error(`Brand record request failed: ${res.status}`)
  const record = parseRecord(await res.json(), realm)
  if (!record) throw new Error('Invalid brand record')
  writeCache(realm, record)
  return record
}

// No flash of the unbranded page: the cached record goes on the root when
// this file loads, before React mounts.
const startupRealm = getRealmFromPath()
if (startupRealm) {
  const cached = readCache(startupRealm)
  if (cached) applyBrandToRoot(cached.record)
}

/**
 * The operator's record on a /u/<alias> page, or null: no alias, or a record
 * that cannot be had, which leaves the page Parkeze's.
 */
export function useBranding() {
  const realm = getRealmFromPath()
  const cached = realm ? readCache(realm) : null

  const { data: brand, isLoading } = useQuery({
    queryKey: ['brand-record', CACHE_SCHEMA, realm],
    queryFn: () => fetchRecord(realm!),
    enabled: !!realm,
    initialData: cached?.record,
    // The cached record is as old as its fetch, so a stale one is refetched
    // (and shown meanwhile).
    initialDataUpdatedAt: cached?.fetchedAt,
    staleTime: STALE_MS,
    retry: 1,
  })

  useEffect(() => {
    if (!brand) return
    applyBrandToRoot(brand)
    return removeBrandFromRoot
  }, [brand])

  useEffect(() => {
    if (!brand) return
    const original = document.title
    document.title = `${brand.name} Parking Map`
    return () => { document.title = original }
  }, [brand])

  return { realm, brand: brand ?? null, isLoading }
}
